import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, Link as LinkIcon, Image as ImageIcon,
  List, ListOrdered, Heading2, Quote, Undo, Redo, Eraser,
  AlignLeft, AlignCenter, AlignRight, Trash2,
} from 'lucide-react';
import { AdminAPI, API_URL } from '@/services/api';
import { toast } from 'sonner';

/**
 * Sadə HTML redaktoru: qalın, kursiv, alt xətt, link, şəkil, siyahı, başlıq, sitat, şrift.
 *
 * Niyə kitabxana yox: lazım olan funksiyalar məhduddur (müştəri link + şəkil + bold/italic
 * istəyib), əlavə asılılıq isə hər deploy-da risk deməkdir. `contentEditable` +
 * `document.execCommand` bütün brauzerlərdə işləyir.
 *
 * ⚠️ Buradan çıxan HTML-ə ETİBAR EDİLMİR — server `sanitize.util.ts` ilə təmizləyir.
 */

/*
 * Şrift seçicisi kursorun altındakı ŞRİFTİ GÖSTƏRİR (Word-dəki kimi).
 *
 * ⚠️ Əvvəl burada «əmr menyusu» sınandı (seçimdən sonra başlığa qayıdırdı) —
 * istifadəçiyə seçim baş tutmamış kimi göründü, ATILDI. Bir daha etmə.
 * ⚠️ `<select>` eyni dəyər təkrar seçiləndə `change` VERMİR. Bu, seçicini
 * kursora görə yeniləməklə həll olunur: başqa abzasa keçəndə göstərilən dəyər
 * özü «Standart»a düşür, ona görə Times yenidən seçiləndə hadisə işə düşür.
 *
 * `match` — brauzerin `queryCommandValue('fontName')` cavabını tanımaq üçün.
 */
const FONTS = [
  // `inherit` = mətnin öz şriftinə qayıt (qalın/kursiv/link toxunulmadan qalır)
  { label: 'Standart', value: 'inherit', match: null as RegExp | null },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif', match: /arial/i },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif', match: /times/i },
];

/*
 * Şəklin ölçüsü və düzülüşü SİNİFLƏ saxlanılır, piksellə yox.
 *
 * ⚠️ Səbəb: `width="900"` yazılsa telefonda dizayn sınardı. Sinif isə CSS-dəki
 * qaydaya bağlıdır (`admin/src/index.css` və eyni qaydalar `client/src/index.css`),
 * dar ekranda avtomatik tam enə keçir. Server də yalnız bu sinifləri buraxır.
 */
const SIZE_CLASSES = ['rte-img-sm', 'rte-img-md', 'rte-img-lg', 'rte-img-full'] as const;
const ALIGN_CLASSES = ['rte-img-left', 'rte-img-center', 'rte-img-right'] as const;

const SIZE_BUTTONS = [
  { cls: 'rte-img-sm', label: 'Kiçik' },
  { cls: 'rte-img-md', label: 'Orta' },
  { cls: 'rte-img-lg', label: 'Böyük' },
  { cls: 'rte-img-full', label: 'Tam en' },
] as const;

const ALIGN_BUTTONS = [
  { cls: 'rte-img-left', icon: AlignLeft, title: 'Sola — mətn sağdan axır' },
  { cls: 'rte-img-center', icon: AlignCenter, title: 'Mərkəzə' },
  { cls: 'rte-img-right', icon: AlignRight, title: 'Sağa — mətn soldan axır' },
] as const;

/** Yeni şəkil bu görkəmdə əlavə olunur — tam en deyil, mətnə oturan orta ölçü. */
const DEFAULT_IMG_CLASS = 'rte-img-md rte-img-center';

interface Props {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const RichTextEditor: React.FC<Props> = ({ value, onChange, disabled, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  /** Üzərinə klikləmiş şəkil — ölçü düymələri ona tətbiq olunur */
  const [selectedImg, setSelectedImg] = useState<HTMLImageElement | null>(null);
  /** Sonuncu özümüz göndərdiyimiz HTML — geri qayıdanda kursoru sıçratmamaq üçün */
  const lastEmitted = useRef<string | null>(null);
  /** Seçicidə görünən şrift — kursorun altındakı mətnə görə yenilənir */
  const [currentFont, setCurrentFont] = useState<string>('inherit');

  /**
   * Yüklənmiş mətnin şriftini tapır və seçicidə göstərir.
   *
   * ⚠️ Niyə ayrıca funksiya: mövcud yazı açılanda kursor hələ redaktorda
   * DEYİL, ona görə `queryCommandValue('fontName')` heç nə qaytarmır və
   * seçici «Standart»da qalırdı — mətn əslində Times olsa da. Müştəri bunu
   * «şrift itdi» kimi başa düşürdü (mətn Times görünürdü, seçici standart).
   * Ona görə burada kursor yox, DOM oxunur: ilk mətn parçasının üstündəki
   * inline `font-family` axtarılır (Word sənəd açanda kursoru əvvəldə sayır).
   */
  const detectFontFromContent = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node && !node.textContent?.trim()) node = walker.nextNode();

    let raw = '';
    let parent = node?.parentElement ?? null;
    // yalnız redaktorun içindəki elementlərə baxılır
    while (parent && el.contains(parent)) {
      if (parent.style?.fontFamily) {
        raw = parent.style.fontFamily;
        break;
      }
      parent = parent.parentElement;
    }

    const found = FONTS.find((font) => font.match?.test(raw));
    setCurrentFont(found ? found.value : 'inherit');
  }, []);

  /**
   * Kursor sıçramasın deyə yalnız KƏNARDAN gələn dəyişiklik yazılır.
   * `lastEmitted` yoxlaması vacibdir: `emit` seçim çərçivəsini silib HTML
   * göndərir, DOM-da isə çərçivə qalır — bu fərq olmadan hər yazıda
   * `innerHTML` yenidən qurulardı və kursor mətnin əvvəlinə atılardı.
   */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmitted.current) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
      setSelectedImg(null);
    }
    // mətn kənardan gəldi (yazı açıldı və ya dil dəyişdi) → seçici yenilənir
    detectFontFromContent();
  }, [value, detectFontFromContent]);

  /*
   * ⚠️ `styleWithCSS` BİR DƏFƏ açılır və BİR DAHA SÖNDÜRÜLMÜR.
   *
   * Səbəb (müştərinin şikayəti «Times seçirəm, saxlayanda standart olur»):
   * istifadəçi şrifti seçib SONRA yazanda brauzer «gözləyən yazı stilini»
   * məhz hərfləri yazdığı anda tətbiq edir. Bayraq həmin an sönülü olsa,
   * köhnə `<font face="...">` teqi yaranır, server sanitizasiyası isə onu
   * tanımadığı üçün atır → şrift saxlayan kimi itir. Yəni bayrağı əmrdən
   * sonra geri söndürmək bəs etmirdi, o, DAİM açıq qalmalıdır.
   *
   * Yan təsir: qalın/kursiv artıq `<b>`/`<i>` yox,
   * `<span style="font-weight: bold">` verir — sanitizasiya bu stilləri
   * buraxır (`sanitize.util.ts` → `allowedStyles`), saytda görünüş eynidir.
   */
  useEffect(() => {
    document.execCommand('styleWithCSS', false, 'true');
  }, []);

  /**
   * Kursor hərəkət edəndə seçicidə həmin yerin şrifti göstərilir.
   * Seçim redaktordan kənardadırsa toxunulmur — panelin başqa sahələrinə
   * klikləyəndə göstərilən şrift sıçramasın.
   */
  const detectFont = useCallback(() => {
    const el = editorRef.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return;
    if (!el.contains(selection.anchorNode)) return;
    const raw = String(document.queryCommandValue('fontName') || '');
    const found = FONTS.find((font) => font.match?.test(raw));
    setCurrentFont(found ? found.value : 'inherit');
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', detectFont);
    return () => document.removeEventListener('selectionchange', detectFont);
  }, [detectFont]);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // seçim çərçivəsi saxlanılan HTML-ə DÜŞMƏMƏLİDİR
    const marked = Array.from(el.querySelectorAll('img[data-rte-selected]'));
    marked.forEach((img) => img.removeAttribute('data-rte-selected'));
    const html = el.innerHTML;
    lastEmitted.current = html;
    onChange(html);
    marked.forEach((img) => img.setAttribute('data-rte-selected', ''));
  }, [onChange]);

  const exec = (command: string, argument?: string) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, argument);
    emit();
  };

  /** Şəkli seçir və çərçivəyə alır (çərçivə yalnız redaktorun içindədir). */
  const selectImage = (img: HTMLImageElement | null) => {
    const el = editorRef.current;
    el?.querySelectorAll('img[data-rte-selected]').forEach((other) =>
      other.removeAttribute('data-rte-selected'),
    );
    if (img) img.setAttribute('data-rte-selected', '');
    setSelectedImg(img);
  };

  const handleEditorClick = (event: React.MouseEvent) => {
    if (disabled) return;
    const target = event.target as HTMLElement;
    selectImage(target.tagName === 'IMG' ? (target as HTMLImageElement) : null);
  };

  /** Seçilmiş şəklə ölçü/düzülüş sinfi verir — eyni qrupdakı köhnəsi silinir. */
  const applyImageClass = (cls: string, group: readonly string[]) => {
    if (!selectedImg) return;
    group.forEach((c) => selectedImg.classList.remove(c));
    selectedImg.classList.add(cls);
    emit();
  };

  const removeImage = () => {
    if (!selectedImg) return;
    selectedImg.remove();
    setSelectedImg(null);
    emit();
  };

  const insertLink = () => {
    const selection = window.getSelection()?.toString();
    if (!selection) {
      toast.error('Əvvəlcə linkə çevriləcək mətni seçin');
      return;
    }
    const url = window.prompt('Link ünvanı:', 'https://');
    if (!url) return;
    // yalnız təhlükəsiz sxemlər — `javascript:` linki serverdə də atılır, amma burada da dayandırırıq
    if (!/^(https?:|mailto:)/i.test(url)) {
      toast.error('Yalnız http, https və mailto ünvanları olar');
      return;
    }
    exec('createLink', url);
  };

  const handleImagePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // eyni faylı təkrar seçmək mümkün olsun
    if (!file) return;

    try {
      setUploading(true);
      const res = await AdminAPI.uploads.uploadImage(file);
      // `/api/v1` sonluğu atılır: `res.data.url` onsuz da tam yoldur
      const url = `${API_URL.replace(/\/api\/v1\/?$/, '')}${res.data.url}`;

      // `insertImage` çılpaq <img> qoyur; sinifləri birlikdə vermək üçün HTML yazırıq
      const safeUrl = url.replace(/"/g, '&quot;');
      exec('insertHTML', `<img src="${safeUrl}" class="${DEFAULT_IMG_CLASS}" alt="">`);

      // yeni şəkil dərhal seçilsin ki, ölçü düymələri əlinin altında olsun
      const images = editorRef.current?.querySelectorAll('img');
      const inserted = images ? (Array.from(images).pop() as HTMLImageElement | undefined) : undefined;
      if (inserted) selectImage(inserted);

      toast.success('Şəkil əlavə olundu — ölçüsünü aşağıdakı düymələrlə dəyişin');
    } catch (error) {
      console.error(error);
      toast.error('Şəkli yükləmək alınmadı');
    } finally {
      setUploading(false);
    }
  };

  const applyFont = (fontFamily: string) => {
    if (disabled || !fontFamily) return;
    editorRef.current?.focus();
    // başqa redaktor nüsxəsi bayrağı söndürübsə, tətbiqdən əvvəl bərpa edilir
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('fontName', false, fontFamily);
    setCurrentFont(fontFamily);
    emit();
  };

  const buttons = [
    { icon: Bold, title: 'Qalın (Ctrl+B)', action: () => exec('bold') },
    { icon: Italic, title: 'Kursiv (Ctrl+I)', action: () => exec('italic') },
    { icon: Underline, title: 'Alt xətt (Ctrl+U)', action: () => exec('underline') },
    { icon: Heading2, title: 'Başlıq', action: () => exec('formatBlock', '<h2>') },
    { icon: List, title: 'Nöqtəli siyahı', action: () => exec('insertUnorderedList') },
    { icon: ListOrdered, title: 'Nömrəli siyahı', action: () => exec('insertOrderedList') },
    { icon: Quote, title: 'Sitat', action: () => exec('formatBlock', '<blockquote>') },
    { icon: LinkIcon, title: 'Link əlavə et', action: insertLink },
    { icon: ImageIcon, title: 'Şəkil yüklə', action: () => fileInputRef.current?.click() },
    { icon: Eraser, title: 'Formatı təmizlə', action: () => exec('removeFormat') },
    { icon: Undo, title: 'Geri al', action: () => exec('undo') },
    { icon: Redo, title: 'İrəli', action: () => exec('redo') },
  ];

  const hasClass = (cls: string) => selectedImg?.classList.contains(cls) ?? false;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-1 flex-wrap p-2 border-b border-white/10">
        {buttons.map((button) => (
          <button
            key={button.title}
            type="button"
            title={button.title}
            disabled={disabled || uploading}
            onMouseDown={(e) => e.preventDefault()} // seçim itməsin
            onClick={button.action}
            className="p-2 hover:bg-white/10 rounded-lg text-muted-foreground hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <button.icon className="w-4 h-4" />
          </button>
        ))}

        <span className="ml-2 text-xs text-muted-foreground">Şrift:</span>
        <select
          disabled={disabled}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => applyFont(e.target.value)}
          value={currentFont}
          title="Şrift"
          className="bg-background/50 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-foreground disabled:opacity-30"
        >
          {FONTS.map((font) => (
            <option key={font.label} value={font.value}>{font.label}</option>
          ))}
        </select>

        {uploading && <span className="text-xs text-muted-foreground ml-2">Şəkil yüklənir…</span>}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImagePick}
        />
      </div>

      {/*
        Şəkil alətləri — yalnız şəklə klikləyəndə görünür.
        Üzən (floating) panel yerinə sabit sətir seçilib: mövqe hesablaması
        sürüşdürəndə sürüşür, bu isə həmişə eyni yerdədir və telefonda da işləyir.
      */}
      {selectedImg ? (
        <div
          data-testid="rte-image-toolbar"
          className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-white/10 bg-primary/5"
        >
          <span className="text-xs text-muted-foreground mr-1">Seçilmiş şəkil:</span>

          {SIZE_BUTTONS.map((size) => (
            <button
              key={size.cls}
              type="button"
              data-size={size.cls}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyImageClass(size.cls, SIZE_CLASSES)}
              className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${
                hasClass(size.cls)
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'text-muted-foreground hover:text-white hover:bg-white/10 border border-transparent'
              }`}
            >
              {size.label}
            </button>
          ))}

          <span className="w-px h-5 bg-white/10 mx-1" />

          {ALIGN_BUTTONS.map((align) => (
            <button
              key={align.cls}
              type="button"
              data-align={align.cls}
              title={align.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyImageClass(align.cls, ALIGN_CLASSES)}
              className={`p-1.5 rounded-lg transition-colors ${
                hasClass(align.cls)
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'text-muted-foreground hover:text-white hover:bg-white/10 border border-transparent'
              }`}
            >
              <align.icon className="w-4 h-4" />
            </button>
          ))}

          <button
            type="button"
            title="Şəkli sil"
            onMouseDown={(e) => e.preventDefault()}
            onClick={removeImage}
            className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="px-3 py-1.5 border-b border-white/10 text-xs text-muted-foreground">
          Şəklin ölçüsünü dəyişmək üçün onun üzərinə klikləyin.
        </div>
      )}

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onClick={handleEditorClick}
        data-placeholder={placeholder}
        className="rte-content flex-1 overflow-y-auto p-6 focus:outline-none text-sm leading-relaxed disabled:opacity-50"
      />
    </div>
  );
};
